export default class DateTimePicker {
  static instances = new WeakMap();

  constructor(input, options = {}) {
    if (!(input instanceof HTMLElement)) return;

    if (DateTimePicker.instances.has(input)) {
      return DateTimePicker.instances.get(input);
    }

    this.input = input;
    this.options = options;

    this.mode = options.mode || 'datetime';
    this.minDate = options.minDate || null;

    this.current = new Date();
    this.selectedDate = null;

    this.parseInitialValue();

    this.build();
    this.bind();

    DateTimePicker.instances.set(input, this);
  }

  /* ---------------- INIT VALUE ---------------- */

  parseInitialValue() {
    const val = this.input.value;
    if (!val) return;

    const parsed = val.replace(' ', 'T');
    const date = new Date(parsed);

    if (!isNaN(date)) {
      this.selectedDate = date;
      this.current = new Date(date);
    }
  }

  /* ---------------- HELPERS ---------------- */

  isBeforeMin(date) {
    if (!this.minDate) return false;

    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const min = new Date(this.minDate);
    min.setHours(0, 0, 0, 0);

    return d < min;
  }

  /* ---------------- UI ---------------- */

  build() {
    this.panel = document.createElement('div');
    this.panel.className = 'dt-panel hidden';

    this.panel.innerHTML = `
            <div class="dt-header">
                <button type="button" data-prev>‹</button>
                <span class="dt-month"></span>
                <button type="button" data-next>›</button>
            </div>

            <div class="dt-calendar"></div>

            ${this.mode === 'datetime' ? `
            <div class="dt-time">
                <input type="number" min="0" max="23" data-hours> :
                <input type="number" min="0" max="59" data-minutes>
            </div>` : ''}

            <button type="button" data-apply>OK</button>
        `;

    document.body.appendChild(this.panel);

    this.calendar = this.panel.querySelector('.dt-calendar');
    this.monthLabel = this.panel.querySelector('.dt-month');

    this.hours = this.panel.querySelector('[data-hours]');
    this.minutes = this.panel.querySelector('[data-minutes]');

    if (this.mode === 'datetime') {
      const d = this.selectedDate || new Date();
      this.hours.value = String(d.getHours()).padStart(2, '0');
      this.minutes.value = String(d.getMinutes()).padStart(2, '0');
    }
  }

  /* ---------------- EVENTS ---------------- */

  bind() {
    this.input.addEventListener('click', () => this.open());

    this.panel.querySelector('[data-prev]').onclick = () => {
      this.current.setMonth(this.current.getMonth() - 1);
      this.render();
    };

    this.panel.querySelector('[data-next]').onclick = () => {
      this.current.setMonth(this.current.getMonth() + 1);
      this.render();
    };

    this.panel.querySelector('[data-apply]').onclick = () => this.apply();

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.dt-panel') && e.target !== this.input) {
        this.close();
      }
    });

    window.addEventListener('resize', () => this.handleViewportChange());
    window.addEventListener('orientationchange', () => this.handleViewportChange());
  }

  /* ---------------- CORE ---------------- */

  open() {
    const isMobile = window.innerWidth <= 768;

    if (!isMobile) {
      const rect = this.input.getBoundingClientRect();

      this.panel.style.top = rect.bottom + window.scrollY + 'px';
      this.panel.style.left = rect.left + window.scrollX + 'px';
      this.panel.style.transform = '';
    }

    this.panel.classList.remove('hidden');
    this.render();
  }
  close() {
    this.panel.classList.add('hidden');
  }

  render() {
    this.calendar.innerHTML = '';

    const year = this.current.getFullYear();
    const month = this.current.getMonth();

    this.monthLabel.textContent =
      `${year}-${String(month + 1).padStart(2, '0')}`;

    const firstDay = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();

    for (let i = 0; i < firstDay; i++) {
      this.calendar.appendChild(document.createElement('div'));
    }

    for (let d = 1; d <= days; d++) {
      const el = document.createElement('div');
      const date = new Date(year, month, d);

      el.textContent = d;

      const isSelected =
        this.selectedDate &&
        this.selectedDate.getFullYear() === year &&
        this.selectedDate.getMonth() === month &&
        this.selectedDate.getDate() === d;

      if (isSelected) el.classList.add('selected');

      // blokada przeszłości
      if (this.isBeforeMin(date)) {
        el.classList.add('disabled');
      } else {
        el.onclick = () => {
          this.selectedDate = date;

          this.calendar.querySelectorAll('div')
            .forEach(x => x.classList.remove('selected'));

          el.classList.add('selected');
        };
      }

      this.calendar.appendChild(el);
    }
  }

  apply() {
    if (!this.selectedDate) return;

    // walidacja końcowa
    if (this.isBeforeMin(this.selectedDate)) return;

    const yyyy = this.selectedDate.getFullYear();
    const mm = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
    const dd = String(this.selectedDate.getDate()).padStart(2, '0');

    let value = `${yyyy}-${mm}-${dd}`;

    if (this.mode === 'datetime') {
      const h = String(this.hours.value).padStart(2, '0');
      const m = String(this.minutes.value).padStart(2, '0');
      value += ` ${h}:${m}`;
    }

    this.input.value = value;
    this.close();
  }

  handleViewportChange() {
    if (this.panel.classList.contains('hidden')) return;

    const isMobile = window.innerWidth <= 768;

    if (!isMobile) {
      const rect = this.input.getBoundingClientRect();

      this.panel.style.top = rect.bottom + window.scrollY + 'px';
      this.panel.style.left = rect.left + window.scrollX + 'px';
      this.panel.style.transform = '';
    }
  }
}



/* ---------------- INIT ---------------- */

export function initPickers() {
  const today = new Date();

  document.querySelectorAll('.datetime').forEach(el => {
    new DateTimePicker(el, {
      mode: 'datetime',
      minDate: today
    });
  });

  document.querySelectorAll('.date').forEach(el => {
    new DateTimePicker(el, {
      mode: 'date',
      minDate: today
    });
  });
}